/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */
import moment from 'moment';
import Channel from '@phenixrts/sdk/types/sdk/channels/Channel';

import fetch from 'services/fetch.service';
import userStore from 'services/user-store';
import urlPath from 'services/url-path';
import backendInfo from 'services/backend-info.service';
import hostService from 'services/host-url.service';
import {auth} from 'services/auth.service';
import {transformToPortalError} from 'utility/error-handler';

import {
  ChannelList,
  IChannel,
  IMember,
  IMessage,
  IMessagesParams,
  PermalinkType
} from 'interfaces';

const tokenType = {
  auth: 'Auth',
  publish: 'Publish',
  authAndStream: 'AuthAndStream'
};

export const fetchChannels = async(): Promise<ChannelList> => {
  const headers = new Headers();

  headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

  const response = await fetch.put({
    url: urlPath.fetchChannelsUrl,
    headers
  });

  if (!(response.data.channels instanceof Array)) {
    throw new Error('Incorrect type of response. Should be an array');
  }

  return response.data.channels.map((channel: IChannel) => ({
    ...channel,
    created: moment.utc(channel.created),
    lastUpdate: moment.utc(channel.lastUpdate),
    state: {
      isOnline: false,
      numberOfPrimaryPresenters: 0,
      numberOfAlternatePresenters: 0,
      isHighAvailability: false
    }
  }));
};

export const fetchChannelStreams = async(id: string): Promise<IMember[]> => {
  const headers = new Headers();

  headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

  const response = await fetch.get({
    url: urlPath.fetchChannelMembersUrl(id),
    headers
  });

  return response.data.members;
};

export const fetchChannelDetails = async(id: string): Promise<IChannel> => {
  const headers = new Headers();

  headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

  const response = await fetch.get({
    url: urlPath.fetchChannelUrl(id),
    headers
  });

  return response.data.channel;
};

export const generateToken = async(subscriptionData: Record<string, string>, features: string[]): Promise<string> => {
  const uri = hostService.getHostUrl();
  const sessionId = features.includes('streaming') ? '*' : undefined;
  const headers = new Headers();

  headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

  const edgeTokenResponse = await fetch.post({
    url: urlPath.edgeAuth,
    headers,
    data: {
      capabilities: features,
      uri,
      sessionId,
      expiresInSeconds: moment.duration(24, 'hours').asSeconds(),
      ...subscriptionData
    }
  });

  return edgeTokenResponse.data.edgeAuthToken;
};

export const playStream = async(
  subscriptionData: Record<string, string>,
  videoElement: HTMLVideoElement,
  features: string[] = [],
  setChannelPCast: (channel: Channel) => void
): Promise<void> => {
  const token = await generateToken(subscriptionData, features);
  const channel: Channel = token ? window.phenix.Channels.createChannel({
    videoElement,
    token
  }) : null;

  setChannelPCast(channel);
};

export const createChannel = async(alias: string, name: string, description: string, options = []): Promise<IChannel> => {
  const headers = new Headers();

  headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

  const response = await fetch.put({
    url: urlPath.createChannelUrl,
    headers,
    data: {
      channel: {
        alias,
        name,
        description,
        options
      }
    }
  });

  return response.data.channel;
};

export const deleteChannel = async(channelId: string): Promise<{data?: unknown; status?: number; error?: Error}> => {
  const headers = new Headers();

  headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

  return fetch.delete({
    url: `${urlPath.deleteChannelUrl}/${encodeURIComponent(channelId)}`,
    headers
  });
};

export const getAllChannelsState = async(channelIds: string[]): Promise<{channelId: string; isOnline: boolean; multipleStreams: boolean}[]> => {
  const headers = new Headers();

  headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

  const channelsStateResponse = await Promise.all(
    channelIds.map((channelId: string) => fetch.get({
      url: urlPath.stateOfChannelUrl(channelId),
      headers
    }))
  );

  return channelIds.map((channelId, i) => ({
    channelId,
    isOnline: !!+channelsStateResponse[i].data,
    multipleStreams: channelsStateResponse[i].data > 1
  }));
};

interface IChannelMessages {
  data: {
    hasMoreAfter: boolean;
    hasMoreBefore: boolean;
    messages: IMessage[];
    status: string;
  };
}

export const getChannelMessages = async(channelId: string, params?: IMessagesParams): Promise<IChannelMessages> => {
  const defaultMessagesLimit = 100;

  try {
    const queryString = new URLSearchParams({
      ...params,
      limit: String(defaultMessagesLimit)
    }).toString();
    const url = `${urlPath.getChannelMessagesUrl(channelId)}?${queryString}`;
    const headers = new Headers();

    headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

    const response = await fetch.get({
      url,
      headers
    });

    return response.data;
  } catch (e) {
    const portalError = transformToPortalError(e);

    throw portalError;
  }
};

export const getManifestLink = (channelId: string, manifest: string): string => {
  return `${backendInfo.getBackendUriWithPCast()}/channel/${encodeURIComponent(channelId)}/${manifest}`;
};

export const killChannel = async(
  channelId: string,
  data: { reason: string; options: string[] }
): Promise<{status: number; killedMembers?: IMember[]}> => {
  const headers = new Headers();

  headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

  const response = await fetch.put({
    url: urlPath.killChannel(channelId),
    headers,
    data
  });

  return response.data;
};

export const forkChannel = async(
  desinationChannelId: string,
  sourceChannelId: string,
  data: {
    streamCapabilities: string[];
    streamTags: string[];
    options: string[];
  }
): Promise<{status: string; members: IMember[]}> => {
  const headers = new Headers();

  headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

  const response = await fetch.put({
    url: urlPath.forkChannel(desinationChannelId, sourceChannelId),
    headers,
    data
  });

  return response.data;
};

export const terminateStream = async(
  streamId: string,
  reason: string
): Promise<{status: number | string}> => {
  const headers = new Headers();
  const [applicationId, secret] = await Promise.all([
    userStore.get(auth.applicationId),
    userStore.get(auth.secret)
  ]);

  headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

  const response = await fetch.delete({ // NOTE: this request requires credentials to be passed. Does not work with token in headers
    url: urlPath.terminateStream,
    headers,
    data: {
      applicationId,
      secret: atob(secret),
      streamId,
      reason
    }
  });

  return response.data;
};

interface IGeneratePermalinkResponse {
  permalink: string;
  publishToken?: string;
  authToken?: string;
  edgeToken?: string;
}

export const generatePermalink = async(
  type: string,
  data: {[key: string]: string | string[]},
  featureType: string
): Promise<IGeneratePermalinkResponse> => {
  const uri = hostService.getHostUrl();
  const headers = new Headers();

  headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

  if (type === PermalinkType.Publishing) {
    const authTokenData: {[key: string]: string | string[]} = {expiresInSeconds: data.expiresInSeconds};

    if (data.channelId) {
      authTokenData.channelId = data.channelId;
    } else if (data.roomId) {
      authTokenData.roomId = data.roomId;
    }

    const [authResponse, publishResponse] = await Promise.all([
      fetch.post({
        url: urlPath.edgeAuth,
        headers,
        data: {
          ...authTokenData,
          uri,
          type: tokenType.auth
        }
      }),
      fetch.post({
        url: urlPath.edgeAuth,
        headers,
        data: {
          ...data,
          uri,
          type: tokenType.publish
        }
      })
    ]);

    return ({
      permalink: `${backendInfo.backendUri}/${featureType}/publish/?authToken=${authResponse.data.edgeAuthToken}&publishToken=${publishResponse.data.edgeAuthToken}`,
      authToken: authResponse.data.edgeAuthToken,
      publishToken: publishResponse.data.edgeAuthToken
    });
  }

  const authAndStreamResponse = await fetch.post({
    url: urlPath.edgeAuth,
    headers,
    data: {
      ...data,
      uri,
      type: tokenType.authAndStream
    }
  });

  return ({
    permalink: `${backendInfo.backendUri}/${featureType}/?token=${authAndStreamResponse.data.edgeAuthToken}`,
    edgeToken: authAndStreamResponse.data.edgeAuthToken
  });
};

export const getEdgeToken = async(params: {[key: string]: string}) => {
  const headers = new Headers();

  headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

  const response = await fetch.post({
    url: urlPath.edgeAuth,
    headers,
    data: {
      ...params,
      expiresInSeconds: moment.duration(1, 'year').asSeconds(),
      uri: hostService.getHostUrl(),
      applyTags: ['portal']
    }
  });

  return response.data.edgeAuthToken;
};